import type { components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQueryClient } from "@tanstack/react-query";

export type QcSettings = components["schemas"]["QcSettings"];
export type QcQueueEntry = components["schemas"]["QcQueueEntry"];
export type QcDecision = components["schemas"]["QcDecision"];
export type QcHoldReason = components["schemas"]["QcHoldReason"];
// springdoc leaves record components optional in the schema, hence the NonNullable wrapper.
export type QcMode = NonNullable<QcSettings["mode"]>;
export type QcStatus = NonNullable<QcDecision["toStatus"]>;
export type ProfileFlagCode = NonNullable<QcSettings["holdFlagCodes"]>[number];

const SETTINGS_PATH = "/api/v1/quality/qc/settings";
const QUEUE_PATH = "/api/v1/quality/qc/queue";
const DECISION_PATH = "/api/v1/quality/qc/batches/{batchId}/decision";
const DECISIONS_PATH = "/api/v1/quality/qc/batches/{batchId}/decisions";

/** Which statuses a batch can be in that stop it leaving the building (US-31.1). */
export const BLOCKING_STATUSES: QcStatus[] = ["PENDING", "ON_HOLD", "REJECTED"];

export function isQcBlocked(status: QcStatus | undefined): boolean {
  return status !== undefined && BLOCKING_STATUSES.includes(status);
}

export function useQcSettings() {
  const api = useApi();
  return api.query.useQuery("get", SETTINGS_PATH, {});
}

export function useSaveQcSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return api.query.useMutation("put", SETTINGS_PATH, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["get", SETTINGS_PATH] }),
  });
}

export function useQcQueue(params: { page: number; size: number }) {
  const api = useApi();
  return api.query.useQuery("get", QUEUE_PATH, {
    params: { query: { page: params.page, size: params.size } },
  });
}

/**
 * The badge in the sidebar: how many batches are waiting, and nothing else. One row rather than
 * none, because a page of zero would still cost the two per-row reads the board does.
 */
export function useQcQueueCount(enabled = true) {
  const api = useApi();
  return api.query.useQuery("get", QUEUE_PATH, { params: { query: { page: 0, size: 1 } } }, { enabled });
}

export function useQcDecisions(batchId: string, enabled = true) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    DECISIONS_PATH,
    { params: { path: { batchId } } },
    { enabled: enabled && Boolean(batchId) },
  );
}

/**
 * A verdict. Invalidates the board, the log and the batch itself: the decision changes what the
 * packing dialog will accept a second later, and a stale batch card is how somebody ends up pressing
 * "pack" on a batch they have just rejected.
 */
export function useDecideQc() {
  const api = useApi();
  const queryClient = useQueryClient();
  return api.query.useMutation("post", DECISION_PATH, {
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["get", QUEUE_PATH] }),
        queryClient.invalidateQueries({ queryKey: ["get", DECISIONS_PATH] }),
        queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/production/batches/{id}"] }),
        queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/production/batches"] }),
      ]),
  });
}
